import { parseISO, format } from 'date-fns';
import _isString from 'lodash/isString';

import { PARTNER_DASHBOARD_UNLOCK_CLASS_LEVEL_CAMPAIGNS } from 'components/Optimizely/Optimizely.features';
import { SERVER_DATE_FORMAT } from 'constants/logic';

export const CAMPAIGN_TYPE = {
  ALL_USER: 'ALL_USER',
  NEW_USER: 'NEW_USER',
  WINBACK: 'WINBACK',
  TIME_SLOT_ALL_USER: 'TIME_SLOT_ALL_USER',
};

export const CAMPAIGN_LEVEL = {
  CLASS: 'CLASS',
  VENUE: 'VENUE',
};

export const EDITABLE_CAMPAIGN_TYPES = [CAMPAIGN_TYPE.ALL_USER, CAMPAIGN_TYPE.NEW_USER, CAMPAIGN_TYPE.WINBACK];
export const EDITABLE_CAMPAIGN_LEVELS = [CAMPAIGN_LEVEL.VENUE, CAMPAIGN_LEVEL.CLASS];

export const PREFERRED_CAMPAIGN_ORDER = [
  CAMPAIGN_TYPE.NEW_USER,
  CAMPAIGN_TYPE.WINBACK,
  CAMPAIGN_TYPE.TIME_SLOT_ALL_USER,
  CAMPAIGN_TYPE.ALL_USER,
];

export const RECOMMENDED_CAMPAIGNS = [CAMPAIGN_TYPE.NEW_USER];

export const NEW_CAMPAIGNS = [CAMPAIGN_TYPE.TIME_SLOT_ALL_USER];

export function formatDate(date) {
  let datetime = date;
  if (_isString(date)) {
    datetime = parseISO(date);
  }
  return format(datetime, SERVER_DATE_FORMAT);
}

export function canCreateVenueLevel(campaign) {
  const { availableLevels } = campaign;
  return !!availableLevels.find(({ level, available }) => level === CAMPAIGN_LEVEL.VENUE && available);
}

export function canCreateClassLevel(campaign, isAppointmentTypeVenue) {
  const { availableLevels } = campaign;
  const fromBackend = !!availableLevels.find(({ level, available }) => level === CAMPAIGN_LEVEL.CLASS && available);

  const fromFeatureFlag = window[PARTNER_DASHBOARD_UNLOCK_CLASS_LEVEL_CAMPAIGNS];

  return fromBackend && (isAppointmentTypeVenue || fromFeatureFlag);
}

export function getDefaultDiscount(campaign) {
  const { availableLevels } = campaign;
  const venueLevel = availableLevels.find(({ level, available }) => level === CAMPAIGN_LEVEL.VENUE && available);
  const classLevel = availableLevels.find(({ level, available }) => level === CAMPAIGN_LEVEL.CLASS && available);

  return venueLevel?.defaultDiscount || classLevel?.defaultDiscount || 0;
}

export const discountOptions = (() => {
  const options = [];

  for (let i = 60; i >= 10; i -= 5) {
    options.push(i);
  }

  return options;
})();

export const isAvailable = campaign => campaign.availableLevels.find(level => level.available);

export function intervalsOverlap(a, b) {
  return a.startTime < b.endTime && a.endTime > b.startTime;
}

export function intervalOverlapsOthers(intervals = [], index) {
  const interval = intervals[index];
  if (!interval) return false;
  return intervals.some((other, otherIndex) => otherIndex !== index && intervalsOverlap(interval, other));
}

const MINUTES_PER_HOUR = 60;
const END_OF_DAY_MINUTES = 23 * MINUTES_PER_HOUR + 45;

const toMinutes = time => {
  const [hours, minutes] = time.split(':').map(Number);
  return hours * MINUTES_PER_HOUR + minutes;
};

const toTimeString = totalMinutes => {
  const hours = Math.floor(totalMinutes / MINUTES_PER_HOUR);
  const minutes = totalMinutes % MINUTES_PER_HOUR;
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
};

export function getNextTimeSlot(intervals = []) {
  const latestEnd = intervals.reduce((max, { endTime }) => Math.max(max, toMinutes(endTime)), 0);
  const endMinutes = Math.min(latestEnd + MINUTES_PER_HOUR, END_OF_DAY_MINUTES);
  return { startTime: toTimeString(latestEnd), endTime: toTimeString(endMinutes) };
}

const FRONTEND_SUNDAY = 0;
const ISO_SUNDAY = 7;

const toIsoDayOfWeek = day => (Number(day) === FRONTEND_SUNDAY ? ISO_SUNDAY : Number(day));

export const fromIsoDayOfWeek = day => (Number(day) === ISO_SUNDAY ? FRONTEND_SUNDAY : Number(day));

export function timeSlotsToServerPayload(timeSlots = {}) {
  return Object.entries(timeSlots)
    .filter(([, intervals]) => intervals?.length > 0)
    .flatMap(([dayOfWeek, intervals]) =>
      intervals.map(({ startTime, endTime }) => ({
        dayOfWeek: toIsoDayOfWeek(dayOfWeek),
        startTime,
        endTime,
      }))
    );
}

const toFormTime = time => time.split(':').slice(0, 2).join(':');

export function timeSlotsFromServer(timeSlots = []) {
  return timeSlots.reduce((acc, { dayOfWeek, slots }) => {
    acc[fromIsoDayOfWeek(dayOfWeek)] = slots.map(({ startTime, endTime }) => ({
      startTime: toFormTime(startTime),
      endTime: toFormTime(endTime),
    }));
    return acc;
  }, {});
}
